import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";

/**
 * How the recipe detail page renders a recipe: the full tabbed view, the
 * condensed card a cook prints and takes to the pass (T-806), the simple card
 * that reads left to right from mise en place to the finished dish with each
 * stage a tap away from its steps, or the staged sheet that sets each stage's
 * ingredients beside its steps in full.
 */
export type RecipeViewMode = "detailed" | "card" | "simple" | "staged";

const VIEW_MODES: RecipeViewMode[] = ["detailed", "card", "simple", "staged"];

/** The flow grid and tabular card were merged into the simple card. */
const RETIRED_MODES: Record<string, RecipeViewMode> = { flow: "simple", tabular: "simple" };

const STORAGE_PREFIX = "yeschef_recipe_view_mode";

/**
 * One key per user. Kitchens share tablets, and a chef who prefers the card
 * should not hand that choice to whoever logs in next.
 */
export function recipeViewModeStorageKey(userId?: string): string {
  return userId ? `${STORAGE_PREFIX}:${userId}` : STORAGE_PREFIX;
}

function readViewMode(key: string): RecipeViewMode {
  try {
    const stored = localStorage.getItem(key);
    return VIEW_MODES.find((mode) => mode === stored) ?? (stored ? RETIRED_MODES[stored] : undefined) ?? "detailed";
  } catch {
    // Safari private mode, and any other browser that refuses storage.
    return "detailed";
  }
}

function writeViewMode(key: string, mode: RecipeViewMode): void {
  try {
    localStorage.setItem(key, mode);
  } catch {
    // The preference is a convenience; losing it is not worth an error.
  }
}

/**
 * The preference lives in localStorage rather than on the user record because
 * nothing on the server stores general per-user UI settings, and sign-out only
 * clears tokens — so the choice is still there at the next sign-in.
 */
export function useRecipeViewMode(): {
  viewMode: RecipeViewMode;
  setViewMode: (mode: RecipeViewMode) => void;
} {
  const { user } = useAuth();
  const key = recipeViewModeStorageKey(user?.id);

  const [viewMode, setViewModeState] = useState<RecipeViewMode>(() => readViewMode(key));

  // The user arrives one render after the page does, so the key changes from
  // the anonymous one to theirs. Re-read during render rather than in an
  // effect — the same "reset state on prop change" pattern the app layout uses
  // for the sidebar breakpoint.
  const [lastKey, setLastKey] = useState(key);
  if (lastKey !== key) {
    setLastKey(key);
    setViewModeState(readViewMode(key));
  }

  const setViewMode = (mode: RecipeViewMode) => {
    setViewModeState(mode);
    writeViewMode(key, mode);
  };

  return { viewMode, setViewMode };
}
